// =============================================================
// 演習の学習状態の保存（番号ごと。番号なしはタブごと）
//
// リロードやタブの誤操作で中断しても、同じ番号を選び直せば直前の状態から
// 再開できるようにするための保存先。ブラウザの中だけで完結するので
// ネットワーク瞬断の影響を受けない。
//
// 持ち主（owner）は session.js の getProgressOwner()：番号でログイン中ならその番号（localStorage）、
// 番号なしで進めているタブなら 'guest:<取り組みID>'（sessionStorage＝このタブの間だけ）。
// 番号なしでもリロードで問1に戻らないようにするため（2026-10-12。旧：番号なしは保存しなかった）。
//
// 保存するのは「どこまで進んだか」だけ（現在ステップ・到達済みステップ・
// 選んだ部品・完了フラグ・カメラ位置）。デッサン画像のような再生成できる
// 重いデータは保存しない。
//
// 有効期限はセッションと同じ3時間。授業をまたいだ古い状態が復活しないようにする。
// 明示的にログアウトしたときは期限を待たずに消す（clearAllProgress）。
// 1日の実験で同じ番号を別の学習者が使い回すため、ログアウト＝その番号の学習は
// 終わりとみなし、次にその番号でログインした人は必ず問1から始める。
// =============================================================

import { SESSION_TTL_MS, progressStorage } from './session';
import { clearTiming } from './stepTiming';

const PREFIX = 'ex2026Progress';

// 演習ごとの保存キー（Ex1_2026_Shape / Ex1_2026_Gaze が使う識別子）
export const EXERCISE_KEYS = ['shape', 'gaze'];

function storageKey(owner, exerciseKey) {
  return `${PREFIX}:${owner}:${exerciseKey}`;
}

export function loadProgress(owner, exerciseKey) {
  if (owner == null) return null;
  const key = storageKey(owner, exerciseKey);
  try {
    const store = progressStorage(owner);
    const raw = store.getItem(key);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || typeof saved !== 'object') return null;
    if (!Number.isFinite(saved.savedAt) || Date.now() - saved.savedAt > SESSION_TTL_MS) {
      store.removeItem(key);
      return null;
    }
    return saved;
  } catch (_) {
    return null;
  }
}

export function saveProgress(owner, exerciseKey, state) {
  if (owner == null) return;
  try {
    progressStorage(owner).setItem(
      storageKey(owner, exerciseKey),
      JSON.stringify({ ...state, savedAt: Date.now() })
    );
  } catch (_) {
    // 保存できなくても演習は続行できる（再開できないだけ）
  }
}

/** その演習の進行状態と所要時間の記録を消す（ログアウトのとき）。 */
export function clearProgress(owner, exerciseKey) {
  if (owner == null) return;
  try {
    progressStorage(owner).removeItem(storageKey(owner, exerciseKey));
  } catch (_) {
    // 何もできない
  }
  clearTiming(owner, exerciseKey);
}

/**
 * その番号の全演習の進行状態を消す。
 * 明示的なログアウト時に呼ぶ：1日の実験で同じ番号を別の学習者が使い回すため、
 * ログアウト後は必ず問1から始まるようにする（前の人の続きを引き継がせない）。
 */
export function clearAllProgress(owner) {
  EXERCISE_KEYS.forEach((key) => clearProgress(owner, key));
}

/** その番号に「続きから再開できる」状態が残っているか（ログイン画面の案内用）。 */
export function hasResumableProgress(owner) {
  if (owner == null) return false;
  return EXERCISE_KEYS.some((key) => {
    const saved = loadProgress(owner, key);
    return !!saved && (saved.index > 0 || saved.isFinished);
  });
}
